import type { KustomerCustomAttributes } from 'api/types';

export const KUSTOMER_SCRIPT_ID = 'forethought-kustomer';
export const KUSTOMER_CORE_SDK = 'https://cdn.kustomerapp.com/chat-web/core.js';

export interface KustomerSettings {
  activeAssistant: string;
  brandId: string;
  closableChat: boolean;
  colors: Record<string, unknown>;
  embedIconColor: string;
  embedIconUrl: string;
  greeting: string;
  knowledgeBaseId: string;
  noHistory: boolean;
  offhoursImageUrl: string;
  offhoursMessage: string;
  showBrandingIdentifier: boolean;
  singleSessionChat: boolean;
  teamIconUrl: string;
  teamName: string;
  volumeControl: {
    enabled: boolean;
    followUpChannels: string[];
    hideWaitOption: boolean;
    markDoneAfterTimeout: boolean;
    mode: string;
    promptDelay: number;
    timeout: number;
    upfrontWaitThreshold: number;
    useDynamicWaitMessage: boolean;
  };
  waitMessage: string;
  widgetType: string;
}

export interface KustomerConversationResponse {
  conversationId: string;
  createdAt: number;
  ended: boolean;
  isInAssistantMode?: boolean;
}

interface KustomerSendMessageRequest {
  attachments?: KustomerFile[];
  body?: string;
  conversationId: string;
  initAssistantPayload?: KustomerInitAssistantPayload;
  isInAssistantMode?: boolean;
  lang?: string;
  lastDeflection?: KustomerLastDeflection;
  payload?: string;
}

interface KustomerSendMessageResponse {
  attachments?: KustomerFile[];
  body?: string;
  conversationId: string;
  createdAt: number;
  direction: KustomerDirection;
  messageId: string;
}

interface KustomerDescribeConversationResponse {
  conversationId: string;
  customAttributes: KustomerCustomAttributes;
}

interface KustomerDescribteCustomerResponse {
  custom: KustomerCustomAttributes;
  customerId: string;
  emails: string[];
  phones: string[];
  socials: Record<string, unknown>;
}

interface KustomerAgentTypingActivityResponse {
  conversationId: string;
  typing: boolean;
  user: {
    avatarUrl: string;
    displayName: string;
  };
}

interface KustomerAssistantEndedResponse {
  conversationId: string;
  isInAssistantMode: boolean;
}

interface KustomerFile {
  file: File;
}

type KustomerDirection = 'in' | 'out';

interface KustomerArticle {
  id: string;
  lang: string;
  title: string;
  url: string;
  version: string;
  visited: number;
}

interface KustomerLastDeflection {
  articles: KustomerArticle[];
}

type KustomerTemplate =
  | {
      body: string;
      id: string;
      templateType: 'text';
    }
  | {
      actions: {
        displayText: string;
        value: string;
        valueType: 'text';
      }[];
      body: string;
      id: string;
      templateType: 'quick_replies';
    };

interface KustomerInitialMessage {
  direction: KustomerDirection;
  directionType: string;
  importedAt: string;
  lang: string;
  meta: {
    template: KustomerTemplate;
  };
  trackingId: string;
}

type KustomerInitAssistantPayload = Omit<
  KustomerInitAssistantResponse,
  'attributes'
>;

export interface KustomerInitAssistantResponse {
  assistant: string;
  attributes: {
    avatarUrl: string;
    name: string;
    publicName: string;
  };
  dialog: string;
  initialMessages: KustomerInitialMessage[];
  node: string;
}

interface KustomerReceivedFile {
  attachmentId: string;
  contentLength: number;
  contentType: string;
  name: string;
  url: string;
}

interface KustomerOnMessageReceivedData {
  attachments?: KustomerReceivedFile[];
  body: string;
  conversationId: string;
  createdAt: string;
  direction: KustomerDirection;
  messageId: string;
  meta: {
    articles: KustomerArticle[];
    template?: KustomerTemplate;
  };
  sentBy: {
    avatarUrl: string;
    displayName: string;
    id: string;
    type: string;
  };
}

interface KustomerAvailability {
  availability: 'online' | 'offline' | 'hidden' | 'disabled';
}

interface KustomerLoginResponse {
  customerId?: string;
  email?: string;
  externalId?: string;
  identified: boolean;
}

export interface KustomerGetMessagesResponse {
  conversationId: string;
  messages: KustomerOnMessageReceivedData[];
}

interface KustomerAnswer {
  answer: string;
  id: string;
  prompt: string;
}

export interface KustomerQuestion {
  displayType: 'text' | 'checkbox' | 'radio';
  enumeration: string[];
  enumerationRaw: string[];
  id: string;
  isRequired: boolean;
  prompt: string;
  type: string;
}

export interface KustomerOnSatisfactionReceived {
  conversationId: string;
  form: {
    description: string;
    followupQuestion: string;
    introduction: string;
    name: string;
    questions: KustomerQuestion[];
    ratingPrompt: string;
    scale: {
      labelHigh: string;
      labelLow: string;
      options: number;
      type: string;
    };
  };
  response: {
    answers: KustomerAnswer[];
    rating: number;
  };
  satisfactionId: string;
  timetoken: string;
}

export interface KustomerSubmitSatisfactionForm {
  answers?: KustomerAnswer[];
  rating?: number;
  satisfactionId: string;
  submittedAt?: string;
}

// Kustomer Core SDK and function helpers

export interface KustomerCore {
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreaddlistener
   */
  addListener: <T extends KustomerListenerEvent>(
    event: T,
    callback: KustomerListenerEventCallback<
      KustomerListenerEventCallbackResponse<T>
    >,
  ) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercorecreateconversation
   */
  createConversation: (
    options: {
      assistant?: string;
      brand: string;
      // custom is not in the docs but it does work
      custom?: KustomerCustomAttributes;
      lang?: string;
      title: string;
    },
    callback?: KustomerCoreCallback<KustomerConversationResponse>,
  ) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoredescribeconversation
   */
  describeConversation: (
    options: {
      conversationId: string;
      customAttributes: KustomerCustomAttributes;
    },
    callback?: KustomerCoreCallback<KustomerDescribeConversationResponse>,
  ) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoredescribecustomer
   */
  describeCustomer: (
    options: {
      attributes?: {
        sharedEmails?: string[];
        sharedPhones?: string[];
        sharedSocials?: Record<string, unknown>[];
      };
      customAttributes?: KustomerCustomAttributes;
    },
    callback?: KustomerCoreCallback<KustomerDescribteCustomerResponse>,
  ) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreendconversation
   */
  endConversation: (
    options: {
      conversationId: string;
    },
    callback?: KustomerCoreCallback<KustomerConversationResponse>,
  ) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoregetchatsettings
   */
  getChatSettings: () => KustomerSettings;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoregetmessages
   */
  getMessages: (
    options: {
      conversationId: string;
    },
    callback?: KustomerCoreCallback<KustomerGetMessagesResponse>,
  ) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreinit
   */
  init: (
    options: {
      asistantId?: string;
      brandId?: string;
      scheduleId?: string;
    },
    callback?: KustomerCoreCallback<{ settings: KustomerSettings }>,
  ) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreinitassistant
   */
  initAssistant: (
    options: {
      assistant: string;
    },
    callback?: KustomerCoreCallback<KustomerInitAssistantResponse>,
  ) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreischatavailable
   */
  isChatAvailable: () => KustomerAvailability;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/authenticate-chat-with-jwt-token#verify-if-you-are-logged-in
   */
  isLoggedIn: (
    options: { email: string },
    callback?: KustomerCoreCallback<boolean>,
  ) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercorelogin
   */
  login: (
    options: {
      assistantId?: string;
      brandId?: string;
      jwtToken: string;
    },
    callback?: KustomerCoreCallback<KustomerLoginResponse>,
  ) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercorelogout
   */
  logout: (callback?: KustomerCoreCallback<KustomerLoginResponse>) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoreremovelistener
   */
  removeListener: <T extends KustomerListenerEvent>(
    event: T,
    callback: KustomerListenerEventCallback<
      KustomerListenerEventCallbackResponse<T>
    >,
  ) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoresendmessage
   */
  sendMessage: (
    options: KustomerSendMessageRequest,
    callback?: KustomerCoreCallback<KustomerSendMessageResponse>,
  ) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoresendpresenceactivity
   */
  sendPresenceActivity: (options: { presence: 'online' | 'offline' }) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoresendtypingactivity
   */
  sendTypingActivity: (options: {
    conversationId: string;
    typing: boolean;
  }) => void;
  /**
   * https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomercoresubmitsatisfactionform
   */
  submitSatisfactionForm: (
    options: KustomerSubmitSatisfactionForm,
    callback?: KustomerCoreCallback<KustomerOnSatisfactionReceived>,
  ) => void;
}

type KustomerCoreCallbackApi = keyof Pick<
  KustomerCore,
  | 'createConversation'
  | 'describeConversation'
  | 'describeCustomer'
  | 'endConversation'
  | 'getMessages'
  | 'init'
  | 'initAssistant'
  | 'isLoggedIn'
  | 'login'
  | 'logout'
  | 'sendMessage'
  | 'submitSatisfactionForm'
>;

type KustomerListenerEvent =
  | 'onConversationCreate'
  | 'onConversationEnded'
  | 'onConversationUnended'
  | 'onAssistantEnded'
  | 'onSatisfactionReceived'
  | 'onLogin'
  | 'onLogout'
  | 'onMessageReceived'
  | 'onUnreadCountChange'
  | 'onAgentTypingActivity';

type Nullable<T> = T | null;

export type KustomerListenerEventCallbackResponse<
  K extends KustomerListenerEvent,
> = K extends 'onMessageReceived'
  ? Nullable<KustomerOnMessageReceivedData>
  : K extends 'onConversationCreate'
    ? Nullable<KustomerConversationResponse>
    : K extends 'onConversationEnded'
      ? Nullable<Omit<KustomerConversationResponse, 'isInAssistantMode'>>
      : K extends 'onConversationUnended'
        ? Nullable<Omit<KustomerConversationResponse, 'isInAssistantMode'>>
        : K extends 'onAgentTypingActivity'
          ? Nullable<KustomerAgentTypingActivityResponse>
          : K extends 'onAssistantEnded'
            ? Nullable<KustomerAssistantEndedResponse>
            : K extends 'onSatisfactionReceived'
              ? Nullable<KustomerOnSatisfactionReceived>
              : unknown;

type KustomerCoreFunctionArgsHelper<K extends KustomerCoreCallbackApi> =
  KustomerCore[K] extends (...args: infer P) => unknown ? P : never;

type ExtractCallbackResponseType<K extends keyof KustomerCore> =
  KustomerCore[K] extends (
    options: never,
    callback?: KustomerCoreCallback<infer R>,
  ) => void
    ? R
    : never;

type KustomerCoreCallback<T> = (callbackResponse: T, err?: Error) => void;

type KustomerListenerEventCallback<T> = (
  callbackResponse: T,
  err?: Error,
) => void;

/**
 * Helper function to call window.KustomerCore APIs that have node
 * style callback responses.
 *
 * If the callback is not called within five seconds, we will
 * reject the promise with a timeout error.
 */
export const applyKustomerCoreFunctionWithCallback = <
  K extends KustomerCoreCallbackApi,
>(
  fn: K,
  ...args: KustomerCoreFunctionArgsHelper<K>
) => {
  if (!window.KustomerCore) {
    return Promise.reject(new Error('window.KustomerCore is undefined'));
  }

  const kustomerCorePromise = new Promise<ExtractCallbackResponseType<K>>(
    (resolve, reject) => {
      try {
        window.KustomerCore?.[fn](
          // @ts-expect-error TS2556
          ...args,
          (res: ExtractCallbackResponseType<K>, err?: Error) => {
            if (err) {
              return reject(err);
            }
            resolve(res);
          },
        );
      } catch (err) {
        reject(err);
      }
    },
  );

  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(
      () => reject(new Error(`Kustomer Core SDK ${fn} timed out`)),
      5000,
    ),
  );

  return Promise.race([kustomerCorePromise, timeoutPromise]);
};

export const sendKustomerMessage = async (
  options: KustomerSendMessageRequest,
) => {
  return await applyKustomerCoreFunctionWithCallback('sendMessage', options);
};
